import type { FormatConfig } from '../models/chart-config.model';
import type { TableResult } from '../models/chart-data.model';
import type { AnalyticsSchemaSource } from '../models/analytics-schema.model';
import { canonicalDataSourceMetaKey } from '@jepto/shared/utils/data-source-icon-meta.util';

import {
	formatTableCellDisplayValue,
	resolveTableColumnHeaderLabel,
	tableGridColumnHeaderOptions,
	type TableLabelSchemaContext,
} from './table-column-display.util';
import { TABLE_EMPTY_CELL_LABEL } from './table-grid.constants';
import {
	buildTableStackedComparisonCellHtml,
	resolveInverseMetricForField,
} from './table-comparison-display.util';
import {
	isTableComparisonColumnId,
	tableComparisonPrevColumnId,
} from './table-column.util';

export type TableColumnDefLike = NonNullable<TableResult['columnDefs']>[number];

export type FormatPresetId =
	| 'custom'
	| 'currency'
	| 'decimal'
	| 'percentage'
	| 'whole_number'
	| 'compact'
	| 'plain_text';

/** Legacy preset ids still returned by AppSync or older saved UI state. */
export type LegacyFormatPresetId =
	| 'integer' // → whole_number
	| 'percent_fraction' // → percentage (displayScale: fraction)
	| 'percent_value' // → percentage (displayScale: percent)
	| 'roas'; // → custom decimal with suffix "x"

/** Canonical shape for the "Percentage" preset (fraction scale: 0.05 → 5%). */
const PERCENTAGE_PRESET_FORMAT = {
	type: 'percentage' as const,
	precision: 2,
	displayScale: 'fraction' as const,
	suffix: '%',
	grouping: true,
	compact: false,
};

/**
 * Maps persisted / legacy preset ids to the current {@link FormatPresetId} union.
 * Backend and older clients may still send renamed/removed preset strings.
 */
export function normalizeFormatPresetId(presetId: string): FormatPresetId {
	switch (presetId) {
		case 'integer':
		case 'whole_number':
			return 'whole_number';
		case 'percent_fraction':
		case 'percent_value':
		case 'percentage':
			return 'percentage';
		case 'currency':
		case 'decimal':
		case 'compact':
		case 'plain_text':
		case 'custom':
			return presetId;
		case 'roas':
			// No first-class ROAS preset anymore; format details live on FormatConfig.
			return 'custom';
		default:
			return 'custom';
	}
}

export interface FormatValueContext {
	locale?: string;
	accountCurrency?: string;
}

export interface FormatPresetUiFlags {
	showCurrency: boolean;
	showValueScale: boolean;
	suffixLocked: boolean;
	suffixOptions: readonly string[];
}

export interface FormatPresetResult {
	format: FormatConfig;
	ui: FormatPresetUiFlags;
	kind?: 'METRIC' | 'DIMENSION';
}

const DEFAULT_FORMAT_BY_TYPE: Record<
	NonNullable<FormatConfig['type']>,
	Pick<FormatConfig, 'precision' | 'grouping' | 'suffix' | 'compact'>
> = {
	number: { precision: 0, grouping: true, suffix: '', compact: false },
	decimal: { precision: 2, grouping: true, suffix: '', compact: false },
	currency: { precision: 2, grouping: true, suffix: '', compact: false },
	percentage: { precision: 2, grouping: true, suffix: '%', compact: false },
	string: {},
};

export const FORMAT_PRESET_OPTIONS: ReadonlyArray<{
	value: FormatPresetId;
	label: string;
}> = [
	{ value: 'custom', label: 'Custom...' },
	{ value: 'currency', label: 'Currency — $1,234.56' },
	{ value: 'decimal', label: 'Decimal — 1,234.56' },
	{ value: 'percentage', label: 'Percentage — 5.23%' },
	{ value: 'whole_number', label: 'Whole number — 1,234' },
	{ value: 'compact', label: 'Compact — 1.2M' },
	{ value: 'plain_text', label: 'Plain text' },
];

export const FORMAT_TYPE_OPTIONS: ReadonlyArray<{
	value: NonNullable<FormatConfig['type']>;
	label: string;
}> = [
	{ value: 'number', label: 'Number' },
	{ value: 'decimal', label: 'Decimal' },
	{ value: 'currency', label: 'Currency' },
	{ value: 'percentage', label: 'Percentage' },
];

export const FORMAT_PRECISION_OPTIONS = Array.from({ length: 7 }, (_, i) => ({
	value: i,
	label: i === 1 ? '1 decimal' : `${i} decimals`,
}));

export const FORMAT_CURRENCY_OPTIONS: ReadonlyArray<{
	value: string;
	label: string;
}> = [
	{ value: '', label: 'None' },
	{ value: 'AED', label: 'AED — UAE Dirham' },
	{ value: 'AFN', label: 'AFN — Afghan Afghani' },
	{ value: 'AMD', label: 'AMD — Armenian Dram' },
	{ value: 'ARS', label: 'ARS — Argentine Peso' },
	{ value: 'AUD', label: 'AUD — Australian Dollar' },
	{ value: 'AZN', label: 'AZN — Azerbaijani Manat' },
	{ value: 'BBD', label: 'BBD — Barbadian Dollar' },
	{ value: 'BDT', label: 'BDT — Bangladeshi Taka' },
	{ value: 'BGN', label: 'BGN — Bulgarian Lev' },
	{ value: 'BHD', label: 'BHD — Bahraini Dinar' },
	{ value: 'BND', label: 'BND — Brunei Dollar' },
	{ value: 'BOB', label: 'BOB — Bolivian Boliviano' },
	{ value: 'BRL', label: 'BRL — Brazilian Real' },
	{ value: 'CAD', label: 'CAD — Canadian Dollar' },
	{ value: 'CHF', label: 'CHF — Swiss Franc' },
	{ value: 'CLP', label: 'CLP — Chilean Peso' },
	{ value: 'CNY', label: 'CNY — Chinese Yuan (Renminbi)' },
	{ value: 'COP', label: 'COP — Colombian Peso' },
	{ value: 'CRC', label: 'CRC — Costa Rican Colon' },
	{ value: 'CZK', label: 'CZK — Czech Koruna' },
	{ value: 'DKK', label: 'DKK — Danish Krone' },
	{ value: 'DOP', label: 'DOP — Dominican Peso' },
	{ value: 'EGP', label: 'EGP — Egyptian Pound' },
	{ value: 'EUR', label: 'EUR — Euro' },
	{ value: 'GBP', label: 'GBP — Pound Sterling' },
	{ value: 'GEL', label: 'GEL — Georgian Lari' },
	{ value: 'GHS', label: 'GHS — Ghanaian Cedi' },
	{ value: 'GTQ', label: 'GTQ — Guatemalan Quetzal' },
	{ value: 'HKD', label: 'HKD — Hong Kong Dollar' },
	{ value: 'HNL', label: 'HNL — Honduran Lempira' },
	{ value: 'HRK', label: 'HRK — Croatian Kuna' },
	{ value: 'HUF', label: 'HUF — Hungarian Forint' },
	{ value: 'IDR', label: 'IDR — Indonesian Rupiah' },
	{ value: 'ILS', label: 'ILS — Israeli New Shekel' },
	{ value: 'INR', label: 'INR — Indian Rupee' },
	{ value: 'IQD', label: 'IQD — Iraqi Dinar' },
	{ value: 'IRR', label: 'IRR — Iranian Rial' },
	{ value: 'JMD', label: 'JMD — Jamaican Dollar' },
	{ value: 'JOD', label: 'JOD — Jordanian Dinar' },
	{ value: 'JPY', label: 'JPY — Japanese Yen' },
	{ value: 'KES', label: 'KES — Kenyan Shilling' },
	{ value: 'KHR', label: 'KHR — Cambodian Riel' },
	{ value: 'KRW', label: 'KRW — South Korean Won' },
	{ value: 'KWD', label: 'KWD — Kuwaiti Dinar' },
	{ value: 'KZT', label: 'KZT — Kazakhstani Tenge' },
	{ value: 'LAK', label: 'LAK — Lao Kip' },
	{ value: 'LBP', label: 'LBP — Lebanese Pound' },
	{ value: 'LKR', label: 'LKR — Sri Lankan Rupee' },
	{ value: 'MAD', label: 'MAD — Moroccan Dirham' },
	{ value: 'MMK', label: 'MMK — Myanmar Kyat' },
	{ value: 'MNT', label: 'MNT — Mongolian Tugrik' },
	{ value: 'MXN', label: 'MXN — Mexican Peso' },
	{ value: 'MYR', label: 'MYR — Malaysian Ringgit' },
	{ value: 'NGN', label: 'NGN — Nigerian Naira' },
	{ value: 'NIO', label: 'NIO — Nicaraguan Cordoba' },
	{ value: 'NOK', label: 'NOK — Norwegian Krone' },
	{ value: 'NPR', label: 'NPR — Nepalese Rupee' },
	{ value: 'NZD', label: 'NZD — New Zealand Dollar' },
	{ value: 'OMR', label: 'OMR — Omani Rial' },
	{ value: 'PAB', label: 'PAB — Panamanian Balboa' },
	{ value: 'PEN', label: 'PEN — Peruvian Sol' },
	{ value: 'PHP', label: 'PHP — Philippine Peso' },
	{ value: 'PKR', label: 'PKR — Pakistani Rupee' },
	{ value: 'PLN', label: 'PLN — Polish Zloty' },
	{ value: 'PYG', label: 'PYG — Paraguayan Guarani' },
	{ value: 'QAR', label: 'QAR — Qatari Riyal' },
	{ value: 'RON', label: 'RON — Romanian Leu' },
	{ value: 'RUB', label: 'RUB — Russian Ruble' },
	{ value: 'SAR', label: 'SAR — Saudi Riyal' },
	{ value: 'SEK', label: 'SEK — Swedish Krona' },
	{ value: 'SGD', label: 'SGD — Singapore Dollar' },
	{ value: 'SYP', label: 'SYP — Syrian Pound' },
	{ value: 'THB', label: 'THB — Thai Baht' },
	{ value: 'TJS', label: 'TJS — Tajikistani Somoni' },
	{ value: 'TND', label: 'TND — Tunisian Dinar' },
	{ value: 'TRY', label: 'TRY — Turkish Lira' },
	{ value: 'TTD', label: 'TTD — Trinidad and Tobago Dollar' },
	{ value: 'TWD', label: 'TWD — New Taiwan Dollar' },
	{ value: 'UAH', label: 'UAH — Ukrainian Hryvnia' },
	{ value: 'USD', label: 'USD — US Dollar' },
	{ value: 'UYU', label: 'UYU — Uruguayan Peso' },
	{ value: 'UZS', label: 'UZS — Uzbekistani Som' },
	{ value: 'VND', label: 'VND — Vietnamese Dong' },
	{ value: 'YER', label: 'YER — Yemeni Rial' },
	{ value: 'ZAR', label: 'ZAR — South African Rand' },
];

const FORMAT_PREFIX_OPTIONS: ReadonlyArray<{
	value: string;
	label: string;
}> = [
	{ value: '', label: 'None' },
	{ value: '$', label: '$' },
	{ value: '€', label: '€' },
	{ value: '£', label: '£' },
	{ value: '¥', label: '¥' },
	{ value: 'A$', label: 'A$' },
	{ value: '~', label: '~' },
	{ value: '±', label: '±' },
];

const FORMAT_SUFFIX_OPTIONS: ReadonlyArray<{
	value: string;
	label: string;
}> = [
	{ value: '', label: 'None' },
	{ value: '%', label: '%' },
	{ value: '×', label: '×' },
	{ value: 'x', label: 'x' },
	{ value: 'pp', label: 'pp' },
	{ value: 'pts', label: 'pts' },
	{ value: 'K', label: 'K' },
	{ value: 'ms', label: 'ms' },
	{ value: 's', label: 's' },
];

export const FORMAT_VALUE_SCALE_OPTIONS: ReadonlyArray<{
	value: NonNullable<FormatConfig['displayScale']>;
	label: string;
}> = [
	{ value: 'fraction', label: 'Decimal (0.0523 → 5.23%)' },
	{ value: 'percent', label: 'Already percent (5.23 → 5.23%)' },
];

const METRIC_SUFFIX_OPTIONS = FORMAT_SUFFIX_OPTIONS;

function metricUiFlags(
	overrides: Partial<FormatPresetUiFlags> = {}
): FormatPresetUiFlags {
	return {
		showCurrency: true,
		showValueScale: false,
		suffixLocked: false,
		suffixOptions: METRIC_SUFFIX_OPTIONS.map(o => o.value),
		...overrides,
	};
}

export function applyFormatPreset(
	presetId: FormatPresetId | LegacyFormatPresetId | string
): FormatPresetResult {
	switch (presetId) {
		// Legacy ROAS: decimal with locked "x" suffix (e.g. 3.2x).
		case 'roas':
			return {
				format: {
					type: 'decimal',
					precision: 2,
					suffix: 'x',
					grouping: true,
					compact: false,
				},
				ui: metricUiFlags({
					suffixLocked: true,
					suffixOptions: ['x'],
				}),
			};
		// Legacy alias for whole_number.
		case 'integer':
			return applyFormatPreset('whole_number');
		// Legacy alias; fraction scale is the default percentage preset.
		case 'percent_fraction':
			return applyFormatPreset('percentage');
		// Legacy preset: percentage type but values are already 0–100 (not 0–1).
		case 'percent_value':
			return {
				format: {
					...PERCENTAGE_PRESET_FORMAT,
					displayScale: 'percent',
				},
				ui: metricUiFlags({
					showCurrency: false,
					showValueScale: true,
					suffixLocked: true,
					suffixOptions: ['%'],
				}),
			};
		case 'whole_number':
			return {
				format: {
					type: 'number',
					precision: 0,
					grouping: true,
					compact: false,
				},
				ui: metricUiFlags(),
			};
		case 'decimal':
			return {
				format: {
					type: 'decimal',
					precision: 2,
					grouping: true,
					compact: false,
				},
				ui: metricUiFlags(),
			};
		case 'currency':
			return {
				format: {
					type: 'currency',
					precision: 2,
					currency: 'USD',
					grouping: true,
					compact: false,
				},
				ui: metricUiFlags(),
			};
		case 'percentage':
			return {
				format: { ...PERCENTAGE_PRESET_FORMAT },
				ui: metricUiFlags({
					showCurrency: false,
					showValueScale: true,
					suffixLocked: true,
					suffixOptions: ['%'],
				}),
			};
		case 'compact':
			return {
				format: {
					type: 'number',
					precision: 1,
					grouping: true,
					compact: true,
				},
				ui: metricUiFlags(),
			};
		case 'plain_text':
			return {
				format: { type: 'string' },
				ui: {
					showCurrency: false,
					showValueScale: false,
					suffixLocked: false,
					suffixOptions: [],
				},
				kind: 'DIMENSION',
			};
		case 'custom':
		default:
			return {
				format: {
					type: 'number',
					precision: 0,
					grouping: true,
					compact: false,
				},
				ui: metricUiFlags(),
			};
	}
}

export function derivePresetFromFormat(
	format: FormatConfig | null | undefined
): FormatPresetId {
	if (!format?.type || format.type === 'string') {
		return 'plain_text';
	}
	if (format.type === 'number' && format.precision === 0 && !format.compact) {
		return 'whole_number';
	}
	if (
		format.type === 'decimal' &&
		format.precision === 2 &&
		format.suffix == null
	) {
		return 'decimal';
	}
	if (
		format.type === 'currency' &&
		format.currency === 'USD' &&
		format.precision === 2
	) {
		return 'currency';
	}
	if (format.type === 'percentage' && format.suffix === '%') {
		const preset = PERCENTAGE_PRESET_FORMAT;
		const scale = format.displayScale ?? preset.displayScale;
		// Only match the named "Percentage" preset when scale + fields align.
		// percent_value (displayScale: 'percent') and other tweaks → 'custom'
		// so the value-scale toggle is driven by FormatConfig, not preset re-apply.
		if (
			scale === preset.displayScale &&
			format.precision === preset.precision &&
			(format.grouping ?? preset.grouping) === preset.grouping &&
			(format.compact ?? preset.compact) === preset.compact
		) {
			return 'percentage';
		}
		return 'custom';
	}
	// Legacy ROAS shape (decimal + "x"); no roas preset id in the dropdown anymore.
	if (
		format.type === 'decimal' &&
		format.suffix === 'x' &&
		format.precision === 2 &&
		format.grouping !== false &&
		!format.compact
	) {
		return 'custom';
	}
	if (format.type === 'number' && format.compact) {
		return 'compact';
	}
	return 'custom';
}

export function resolveFormatUiFlags(
	format: FormatConfig | null | undefined
): FormatPresetUiFlags {
	if (!format || format.type === 'string') {
		return {
			showCurrency: false,
			showValueScale: false,
			suffixLocked: false,
			suffixOptions: [],
		};
	}
	if (format.type === 'percentage') {
		return metricUiFlags({
			showCurrency: false,
			showValueScale: true,
			suffixLocked: true,
			suffixOptions: ['%'],
		});
	}
	if (format.type === 'currency') {
		return metricUiFlags({ showCurrency: true, showValueScale: false });
	}
	if (format.suffix === 'x') {
		return metricUiFlags({
			suffixLocked: true,
			suffixOptions: ['x'],
		});
	}
	return metricUiFlags();
}

function resolveCurrencyCode(
	currency: string | undefined,
	ctx: FormatValueContext
): string {
	if (!currency || currency === 'account') {
		return ctx.accountCurrency ?? 'USD';
	}
	return currency;
}

function formatNumberValue(
	value: number,
	options: {
		precision: number;
		grouping: boolean;
		compact: boolean;
		locale: string;
	}
): string {
	return new Intl.NumberFormat(options.locale, {
		minimumFractionDigits: options.precision,
		maximumFractionDigits: options.precision,
		useGrouping: options.grouping,
		notation: options.compact ? 'compact' : 'standard',
	}).format(value);
}

function formatCurrencyValue(
	value: number,
	options: {
		currency: string;
		precision: number;
		grouping: boolean;
		compact: boolean;
		locale: string;
	}
): string {
	return new Intl.NumberFormat(options.locale, {
		style: 'currency',
		currency: options.currency,
		minimumFractionDigits: options.precision,
		maximumFractionDigits: options.precision,
		useGrouping: options.grouping,
		notation: options.compact ? 'compact' : 'standard',
	}).format(value);
}

/** Resolves wire `pattern` carriers (currency code / percent scale) into display fields. */
function normalizeFormatForDisplay(format: FormatConfig): FormatConfig {
	let normalized = format;
	const pattern = format.pattern?.trim();
	if (pattern) {
		// AppSync stores currency ISO and percent scale in `pattern` on the wire.
		if (format.type === 'currency' && !format.currency) {
			normalized = {
				...normalized,
				currency: pattern,
				pattern: undefined,
			};
		} else if (
			format.type === 'percentage' &&
			!format.displayScale &&
			(pattern === 'fraction' || pattern === 'percent')
		) {
			normalized = {
				...normalized,
				displayScale: pattern,
				pattern: undefined,
			};
		}
	}

	// Older formats used `prefix` as the currency code (e.g. prefix "USD" with no
	// currency field). Promote ISO prefix → currency so Intl does not duplicate it.
	if (normalized.type === 'currency') {
		const prefix = normalized.prefix?.trim();
		if (prefix && /^[A-Za-z]{3}$/.test(prefix)) {
			const iso = prefix.toUpperCase();
			if (
				!normalized.currency ||
				normalized.currency.toUpperCase() === iso
			) {
				normalized = {
					...normalized,
					currency: normalized.currency ?? iso,
					prefix: undefined,
				};
			}
		}
	}

	return normalized;
}

/** Renders a value using {@link FormatConfig} (custom field preview + display). */
export function formatValue(
	value: unknown,
	format: FormatConfig | null | undefined,
	ctx: FormatValueContext = {}
): string {
	if (value == null || value === '') {
		return '—';
	}
	if (!format?.type || format.type === 'string') {
		return String(value);
	}

	const displayFormat = normalizeFormatForDisplay(format);
	if (displayFormat.pattern?.trim()) {
		return String(value);
	}

	const n = Number(value);
	if (!Number.isFinite(n)) {
		return String(value);
	}

	const locale = ctx.locale ?? 'en-US';
	const type = displayFormat.type;
	const defaults = DEFAULT_FORMAT_BY_TYPE[type];
	const precision = displayFormat.precision ?? defaults.precision ?? 0;
	const grouping = displayFormat.grouping ?? defaults.grouping ?? true;
	const compact = displayFormat.compact ?? defaults.compact ?? false;

	let core: string;
	switch (type) {
		case 'currency':
			core = formatCurrencyValue(n, {
				currency: resolveCurrencyCode(displayFormat.currency, ctx),
				precision,
				grouping,
				compact,
				locale,
			});
			break;
		case 'percentage': {
			const scaled =
				displayFormat.displayScale === 'percent' ? n : n * 100;
			core = formatNumberValue(scaled, {
				precision,
				grouping,
				compact: false,
				locale,
			});
			break;
		}
		case 'number':
		case 'decimal':
		default:
			core = formatNumberValue(n, {
				precision,
				grouping,
				compact,
				locale,
			});
	}

	const suffix =
		displayFormat.suffix ??
		(type === 'percentage' ? '%' : (defaults.suffix ?? ''));
	const prefix = displayFormat.prefix?.trim() ?? '';
	if (type === 'currency') {
		// Do not prepend prefix for currency: Intl.NumberFormat already emits the
		// locale symbol/code (e.g. ₱). Prefix is for labels on number/decimal types.
		return `${core}${suffix}`;
	}
	return `${prefix}${core}${suffix}`;
}

export const FORMAT_PREVIEW_SAMPLE_VALUE = 1234.567;

export { TABLE_EMPTY_CELL_LABEL } from './table-grid.constants';

/** `this` context for Highcharts Grid Pro `cells.formatter`. */
export interface TableGridCellFormatterContext {
	value: unknown;
	format: (template: string) => string;
	row?: { data?: Record<string, unknown> };
}

export function isTableCellValueEmpty(value: unknown): boolean {
	return value == null || value === '';
}

function formatConfigForColumnDef(col: TableColumnDefLike): FormatConfig | null {
	if (col.format) {
		return {
			...col.format,
			type: col.format.type as FormatConfig['type'] | undefined,
		};
	}
	const colType = col.type;
	if (
		colType === 'number' ||
		colType === 'decimal' ||
		colType === 'currency' ||
		colType === 'percentage'
	) {
		return { type: colType };
	}
	return null;
}

/** Raw Grid Pro value template (no empty-placeholder wrapper). */
export function cellValueFormatTemplate(col: TableColumnDefLike): string {
	const fmt = col.format?.type ?? col.type;
	if (fmt === 'currency' || col.type === 'currency') {
		const p = col.format?.precision ?? 2;
		return '${value:,.' + p + 'f}';
	}
	if (fmt === 'percentage' || col.type === 'percentage') {
		const p = col.format?.precision ?? 2;
		return '{value:,.' + p + 'f}%';
	}
	if (fmt === 'decimal' || col.type === 'number') {
		const p = col.format?.precision ?? 0;
		return p === 0 ? '{value:,0f}' : '{value:,.' + p + 'f}';
	}
	return '{value}';
}

/** @deprecated Use {@link tableCellFormatterForColumnDef} — format templates lack an `or` helper in Grid Pro. */
function cellFormatForTableColumnDef(col: TableColumnDefLike): string {
	return cellValueFormatTemplate(col);
}

function isTableMetricColumnDef(col: TableColumnDefLike): boolean {
	if (isTableComparisonColumnId(col.id)) {
		return false;
	}
	const fmt = col.format?.type ?? col.type;
	return fmt !== 'string' && col.type !== 'string';
}

function parseTableCellNumber(value: unknown): number | null {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
}

export function tableCellFormatterForColumnDef(
	col: TableColumnDefLike,
	options?: {
		sources?: AnalyticsSchemaSource[];
		comparisonStacked?: boolean;
		fieldNameByColumnId?: ReadonlyMap<string, string>;
		inverseMetric?: boolean;
		formatCtx?: FormatValueContext;
	}
): (this: TableGridCellFormatterContext) => string {
	const valueFormat = cellValueFormatTemplate(col);
	const sources = options?.sources;
	const comparisonStacked = options?.comparisonStacked === true;
	const isMetricColumn = isTableMetricColumnDef(col);

	return function (this: TableGridCellFormatterContext) {
		if (
			comparisonStacked &&
			isMetricColumn &&
			!isTableComparisonColumnId(col.id)
		) {
			const current = parseTableCellNumber(this.value);
			const prevColId = tableComparisonPrevColumnId(col.id);
			const previous = parseTableCellNumber(
				this.row?.data?.[prevColId]
			);
			const fieldName = options?.fieldNameByColumnId?.get(col.id);
			const inverseMetric =
				options?.inverseMetric ??
				resolveInverseMetricForField(fieldName ?? col.id);

			return buildTableStackedComparisonCellHtml({
				current,
				previous,
				format: formatConfigForColumnDef(col),
				formatCtx: options?.formatCtx,
				inverseMetric,
			});
		}

		if (isTableCellValueEmpty(this.value)) {
			return TABLE_EMPTY_CELL_LABEL;
		}
		const fmt = col.format?.type ?? col.type;
		if (
			(fmt === 'string' || col.type === 'string' || fmt == null) &&
			typeof this.value === 'string' &&
			canonicalDataSourceMetaKey(this.value.trim())
		) {
			return formatTableCellDisplayValue(this.value, { sources });
		}

		return this.format(valueFormat);
	};
}

export function cellsOptionsForTableColumnDef(
	col: TableColumnDefLike,
	options?: {
		sources?: AnalyticsSchemaSource[];
		comparisonStacked?: boolean;
		fieldNameByColumnId?: ReadonlyMap<string, string>;
		inverseMetric?: boolean;
		formatCtx?: FormatValueContext;
	}
): { formatter: (this: TableGridCellFormatterContext) => string } {
	return { formatter: tableCellFormatterForColumnDef(col, options) };
}

export interface TableColumnDefsToGridColumnsOptions {
	sortingEnabled?: boolean;
	columnEvents?: Record<string, unknown>;
	/** Labels from `getCard` cardConfig `data` (backend-resolved); not persisted on save. */
	headerLabelsById?: ReadonlyMap<string, string>;
	fieldNameByColumnId?: ReadonlyMap<string, string>;
	schema?: TableLabelSchemaContext;
	/** When true, comparison stacks inside each metric cell (`__prev` hidden). */
	comparisonStacked?: boolean;
}

/** Maps analytics `columnDefs` to Grid Pro `columns` option entries. */
export function tableColumnDefsToGridColumns(
	defs: TableColumnDefLike[],
	options?: TableColumnDefsToGridColumnsOptions
): Array<Record<string, unknown>> {
	const sortingEnabled = options?.sortingEnabled ?? true;
	const columnEvents = options?.columnEvents;

	const headerLabelsById = options?.headerLabelsById;
	const fieldNameByColumnId = options?.fieldNameByColumnId;
	const schema = options?.schema;
	const cellFormatterOptions = {
		...(schema?.sources ? { sources: schema.sources } : {}),
		...(fieldNameByColumnId ? { fieldNameByColumnId } : {}),
		...(options?.comparisonStacked
			? { comparisonStacked: true }
			: {}),
	};

	return defs
		.filter(col => !isTableComparisonColumnId(col.id))
		.map(col => {
		const headerText = resolveTableColumnHeaderLabel({
			columnId: col.id,
			fieldName: fieldNameByColumnId?.get(col.id),
			cardLabel: headerLabelsById?.get(col.id),
			headerName: col.headerName,
			schema,
		});
		const entry: Record<string, unknown> = {
			id: col.id,
			header: tableGridColumnHeaderOptions(headerText),
			cells: cellsOptionsForTableColumnDef(col, cellFormatterOptions),
			sorting: {
				enabled: col.sortable !== false && sortingEnabled,
			},
		};
		if (columnEvents) {
			entry['events'] = columnEvents;
		}
		return entry;
	});
}
